/**
 * use-graph-node-provenance.ts — Derives source/package provenance for a graph node.
 *
 * Looks up a node by ID from the full graph response and extracts
 * source file and package metadata needed by the node tooltip.
 *
 * Note: GraphNode does not currently carry a sourceFileName or packageName field.
 * We surface what's available (category, folderId as sourceFileId) and leave
 * enrichment for a future phase when the backend exposes richer provenance.
 * Follow-up tracked in GH-258.
 */

import { useMemo } from 'react'
import { useGraphData } from '../../hooks/use-graph'

export interface NodeProvenance {
  /** Node display label */
  label: string
  /** Source file identifier (folderId in current schema) */
  sourceFileId: string | null
  /** Human-readable source file name (label of the source node, if found) */
  sourceFileName: string | null
  /** Package / folder name — derived from folderId lookup */
  packageName: string | null
  /** Node category */
  category: string | null
}

/**
 * Returns provenance metadata for a single graph node.
 * Returns null while loading or when node is not found.
 */
export function useGraphNodeProvenance(nodeId: string | null): NodeProvenance | null {
  const { data } = useGraphData()

  return useMemo<NodeProvenance | null>(() => {
    if (!nodeId || !data?.nodes) return null

    const match = data.nodes.find((n: { data: { id: string } }) => n.data.id === nodeId)
    if (!match) return null

    const { id: _id, label, category, folderId } = match.data

    // Attempt to resolve the folder/source node label for a human-readable name
    const sourceNode = folderId
      ? data.nodes.find((n: { data: { id: string } }) => n.data.id === folderId)
      : null

    return {
      label,
      sourceFileId: folderId ?? null,
      sourceFileName: sourceNode?.data.label ?? null,
      packageName: sourceNode?.data.label ?? null,
      category: category ?? null,
    }
  }, [nodeId, data])
}
